import { useCallback, useEffect, useRef, useState } from 'react';
import './DistrictSizeHistogram.scss';

export interface HistogramBucket {
  min: number;
  max: number | null;
  count: number;
}

interface DistrictSizeHistogramProps {
  data: HistogramBucket[];
  /** Currently selected min value (bucket min) */
  selectedMin?: number;
  /** Currently selected max value (bucket max, null = no upper limit) */
  selectedMax?: number | null;
  /** Called when range changes */
  onChange?: (min: number, max: number | null) => void;
  /** Height of the histogram bars area in pixels */
  height?: number;
}

export function DistrictSizeHistogram({
  data,
  selectedMin,
  selectedMax,
  onChange,
  height = 100,
}: DistrictSizeHistogramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'left' | 'right' | 'range' | null>(null);
  const dragStartRef = useRef<{ startX: number; startLeft: number; startRight: number } | null>(null);

  // Find the indices for the selected range
  const minIndex = selectedMin !== undefined
    ? data.findIndex(b => b.min === selectedMin)
    : 0;
  const maxIndex = selectedMax !== undefined && selectedMax !== null
    ? data.findIndex(b => b.max === selectedMax)
    : data.length - 1;

  // Use local state for smooth dragging
  const [leftIndex, setLeftIndex] = useState(minIndex >= 0 ? minIndex : 0);
  const [rightIndex, setRightIndex] = useState(maxIndex >= 0 ? maxIndex : data.length - 1);

  // Sync with props when they change externally
  useEffect(() => {
    const newMinIndex = selectedMin !== undefined
      ? data.findIndex(b => b.min === selectedMin)
      : 0;
    const newMaxIndex = selectedMax !== undefined && selectedMax !== null
      ? data.findIndex(b => b.max === selectedMax)
      : data.length - 1;

    if (newMinIndex >= 0) setLeftIndex(newMinIndex);
    if (newMaxIndex >= 0) setRightIndex(newMaxIndex);
  }, [selectedMin, selectedMax, data]);

  // Find max count for scaling
  const maxCount = Math.max(...data.map(b => b.count), 1);

  // Calculate bucket index from mouse position
  const getIndexFromPosition = useCallback((clientX: number): number => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const bucketWidth = rect.width / data.length;
    const index = Math.floor(x / bucketWidth);
    return Math.max(0, Math.min(data.length - 1, index));
  }, [data.length]);

  // Calculate bucket offset from pixel delta
  const getIndexDelta = useCallback((deltaX: number): number => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const bucketWidth = rect.width / data.length;
    return Math.round(deltaX / bucketWidth);
  }, [data.length]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    if (isDragging === 'range' && dragStartRef.current) {
      // Move entire range
      const deltaX = e.clientX - dragStartRef.current.startX;
      const indexDelta = getIndexDelta(deltaX);
      const rangeSize = dragStartRef.current.startRight - dragStartRef.current.startLeft;

      let newLeft = dragStartRef.current.startLeft + indexDelta;
      let newRight = dragStartRef.current.startRight + indexDelta;

      // Clamp to bounds
      if (newLeft < 0) {
        newLeft = 0;
        newRight = rangeSize;
      }
      if (newRight > data.length - 1) {
        newRight = data.length - 1;
        newLeft = newRight - rangeSize;
      }

      setLeftIndex(newLeft);
      setRightIndex(newRight);
    } else {
      const index = getIndexFromPosition(e.clientX);

      if (isDragging === 'left') {
        // Left handle can't go past right handle
        setLeftIndex(Math.min(index, rightIndex));
      } else if (isDragging === 'right') {
        // Right handle can't go before left handle
        setRightIndex(Math.max(index, leftIndex));
      }
    }
  }, [isDragging, leftIndex, rightIndex, getIndexFromPosition, getIndexDelta, data.length]);

  // Handle mouse up - emit change
  const handleMouseUp = useCallback(() => {
    if (isDragging && onChange) {
      const minBucket = data[leftIndex];
      const maxBucket = data[rightIndex];
      onChange(minBucket.min, maxBucket.max);
    }
    setIsDragging(null);
    dragStartRef.current = null;
  }, [isDragging, leftIndex, rightIndex, data, onChange]);

  // Add/remove global listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Start dragging
  const handleMouseDown = (handle: 'left' | 'right' | 'range') => (e: React.MouseEvent) => {
    e.preventDefault();
    if (handle === 'range') {
      dragStartRef.current = {
        startX: e.clientX,
        startLeft: leftIndex,
        startRight: rightIndex,
      };
    }
    setIsDragging(handle);
  };

  // Format number for display (e.g., 2500 -> "2.5K", 10000 -> "10K")
  const formatNumber = (n: number | null): string => {
    if (n === null) return '30K+';
    if (n >= 1000) {
      const k = n / 1000;
      return Number.isInteger(k) ? `${k}K` : `${k}K`;
    }
    return String(n);
  };

  // Calculate positions as percentages
  const leftPercent = (leftIndex / data.length) * 100;
  const rightPercent = ((rightIndex + 1) / data.length) * 100;

  return (
    <div className="district-size-histogram">
      <div
        className="district-size-histogram__bars"
        ref={containerRef}
        style={{ height }}
      >
        {data.map((bucket, i) => {
          const barHeight = (bucket.count / maxCount) * 100;
          const isInRange = i >= leftIndex && i <= rightIndex;

          return (
            <div
              key={bucket.min}
              className={`district-size-histogram__bar ${isInRange ? 'district-size-histogram__bar--active' : ''}`}
              style={{ height: `${barHeight}%` }}
              data-tooltip-id="main-tooltip"
              data-tooltip-content={`${bucket.count} accounts with ${formatNumber(bucket.min)}-${formatNumber(bucket.max)} students`}
            />
          );
        })}

        <div className="district-size-histogram__handles">
          {/* Connecting line between handles */}
          <div
            className={`district-size-histogram__connector ${isDragging === 'range' ? 'district-size-histogram__connector--dragging' : ''}`}
            style={{ left: `${leftPercent}%`, width: `${rightPercent - leftPercent}%` }}
            onMouseDown={handleMouseDown('range')}
          />

          {/* Left handle */}
          <div
            className={`district-size-histogram__handle ${isDragging === 'left' ? 'district-size-histogram__handle--dragging' : ''}`}
            style={{ left: `${leftPercent}%` }}
            onMouseDown={handleMouseDown('left')}
          >
            <div className="district-size-histogram__handle-knob" />
          </div>

          {/* Right handle */}
          <div
            className={`district-size-histogram__handle ${isDragging === 'right' ? 'district-size-histogram__handle--dragging' : ''}`}
            style={{ left: `${rightPercent}%` }}
            onMouseDown={handleMouseDown('right')}
          >
            <div className="district-size-histogram__handle-knob" />
          </div>
        </div>
      </div>

      {/* Axis labels */}
      <div className="district-size-histogram__axis">
        {data.map((bucket, i) => (
          <div key={bucket.min} className="district-size-histogram__axis-label">
            {formatNumber(bucket.min)}
          </div>
        ))}
        {/* Last label for the final bucket's max */}
        <div className="district-size-histogram__axis-label district-size-histogram__axis-label--last">
          {formatNumber(data[data.length - 1]?.max ?? null)}
        </div>
      </div>

      {/* Axis title */}
      <div className="district-size-histogram__axis-title">
        Number of Students
      </div>
    </div>
  );
}
